"use client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { BestDealOffer } from "@/components/marketplace/best-deal-offer";
import { DealOfferExplorerModal } from "@/components/marketplace/deal-offer-explorer-modal";
import type { OfferExplorerPayload } from "@/components/marketplace/deal-offer-explorer-types";
import {
  dealOfferFormatOptions,
  preferredDealOfferFormat,
  requestedDealOfferFormat,
  selectedDealOfferSummary,
  selectedOutOfStockDealOffer,
} from "@/components/marketplace/deal-offer-format-state";
import { getDealOfferFormatSummariesQueryOptions } from "@/components/marketplace/deal-offer-format-summaries-query";
import { DealOfferFormatTabs } from "@/components/marketplace/deal-offer-format-tabs";
import { DealOfferStoreDisclosure } from "@/components/marketplace/deal-offer-panel-footer";
import {
  getDealOfferSummaryQueryOptions,
  getDealOffersQueryOptions,
  getOutOfStockDealOffersQueryOptions,
  type DealOffersQueryOptionsInput,
  type OutOfStockDealOffersQueryOptionsInput,
} from "@/components/marketplace/deal-offers-query";
import type {
  DealOffer,
  DealOfferFormatOption,
  DealOffersProps,
  DealOfferSummary,
  DealOfferSummarySnapshot,
} from "@/components/marketplace/deal-offers-types";
import { dealOfferFromSummary } from "@/components/marketplace/dealOfferFromSummary";
import { getDealOffersResolvedState } from "@/components/marketplace/get-deal-offers-resolved-state";
import { OutOfStockDealOffer } from "@/components/marketplace/out-of-stock-deal-offer";
import { ReleaseDealOffersContent } from "@/components/marketplace/ReleaseDealOffersContent";
import { shouldRenderTrackedOffers } from "@/components/marketplace/should-render-tracked-offers";
import { UsedOfferSection } from "@/components/marketplace/UsedOfferSection";
import { useCurrency } from "@/hooks/use-currency";
import { useHashModal } from "@/hooks/use-hash-modal";
import { useIsPartnerShare } from "@/hooks/use-is-partner-share";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";
import type { FormatCategoryWithOther } from "@garimpa/lib/formatGroups";

function useDealOffersInput({
  releaseId,
  workId,
  currency,
  format,
}: Pick<DealOffersProps, "releaseId" | "workId" | "format"> & { currency: string }) {
  return releaseId ? { releaseId, currency } : { workId: workId!, currency, format };
}

function getDealOffersModalOwnerId({
  contextReleaseId,
  releaseId,
  workId,
}: {
  contextReleaseId?: string;
  releaseId?: string;
  workId?: string;
}) {
  if (contextReleaseId) return `release:${contextReleaseId}`;
  return releaseId ? `release:${releaseId}` : `work:${workId ?? "unknown"}`;
}

function terminalDealOffersContent(input: {
  emptyState?: ReactNode;
  errorState?: ReactNode;
  loadingState?: ReactNode;
  state: ReturnType<typeof getDealOffersResolvedState>;
}) {
  if (input.state === "loading") return { terminal: true, content: input.loadingState ?? null };
  if (input.state === "error") return { terminal: true, content: input.errorState ?? null };
  if (input.state === "unavailable") return { terminal: true, content: input.emptyState ?? null };
  return { terminal: false, content: null };
}

function directOutOfStockOffersInput(input: {
  contextReleaseId?: string;
  currency: string;
  format?: DealOffersProps["format"];
  releaseId?: string;
  workId?: string;
}): OutOfStockDealOffersQueryOptionsInput {
  if (input.contextReleaseId)
    return { releaseId: input.contextReleaseId, currency: input.currency };
  if (input.releaseId) return { releaseId: input.releaseId, currency: input.currency };
  return {
    workId: input.workId!,
    currency: input.currency,
    format: input.format === "all" ? undefined : input.format,
  };
}

function directDealOffersState(input: {
  bestOffer: DealOffer | null;
  dealsError: boolean;
  dealsLoading: boolean;
  hasOffers: boolean;
  outOfStockError: boolean;
  outOfStockLoading: boolean;
  outOfStockOffer: DealOffer | null;
}) {
  if (input.dealsLoading) return "loading" as const;
  if (!input.bestOffer && input.outOfStockLoading) return "loading" as const;
  if (input.dealsError) return "error" as const;
  if (!input.bestOffer && input.outOfStockError) return "error" as const;
  if (input.hasOffers && input.bestOffer) return "available" as const;
  return input.outOfStockOffer ? ("available" as const) : ("unavailable" as const);
}

function DealOffersBlock({
  bestOffer,
  compareFormat,
  compareOfferCount,
  contextReleaseId,
  offers,
  queryInput,
  releaseId,
  workId,
  currency,
  presentationScope,
  formatOptions,
  formatLinkTarget,
  activeFormat,
  onFormatChange,
}: {
  bestOffer: DealOffer;
  compareFormat?: NonNullable<DealOffer["formatCategory"]>;
  compareOfferCount?: number;
  contextReleaseId?: string;
  offers: DealOffer[];
  queryInput?: DealOffersQueryOptionsInput;
  releaseId?: string;
  workId?: string;
  currency: string;
  presentationScope: DealOffersProps["presentationScope"];
  formatOptions?: DealOfferFormatOption[];
  formatLinkTarget?: DealOffersProps["formatLinkTarget"];
  activeFormat?: FormatCategoryWithOther | null;
  onFormatChange?: (format: FormatCategoryWithOther) => void;
}) {
  const hashModal = useHashModal();
  const modalOwnerId = getDealOffersModalOwnerId({ contextReleaseId, releaseId, workId });
  const openExplorer = (payload: OfferExplorerPayload) =>
    hashModal.open("offer-explorer", payload, { ownerId: modalOwnerId });

  return (
    <div>
      <BestDealOffer
        formatSwitch={
          formatOptions && onFormatChange ? (
            <DealOfferFormatTabs
              variant="segmented"
              showSingleOption
              activeFormat={activeFormat ?? null}
              formatLinkTarget={formatLinkTarget}
              options={formatOptions}
              onSelect={onFormatChange}
            />
          ) : undefined
        }
        compareFormat={compareFormat}
        compareOfferCount={compareOfferCount}
        offer={bestOffer}
        contextReleaseId={contextReleaseId}
        offers={offers}
        currency={currency}
        presentationScope={presentationScope}
        onExplore={(format) =>
          openExplorer({
            offer: bestOffer,
            format: format ?? bestOffer.formatCategory ?? undefined,
          })
        }
      />
      <DealOfferExplorerModal
        offers={offers}
        contextReleaseId={contextReleaseId}
        releaseId={releaseId}
        workId={workId}
        currency={currency}
        ownerId={modalOwnerId}
        queryInput={queryInput}
      />
    </div>
  );
}

function DirectDealOffersResult({
  bestOffer,
  contextReleaseId,
  currency,
  offers,
  outOfStockOffer,
  presentationScope,
  releaseId,
  resolvedEmptyState,
  resolvedErrorState,
  resolvedFooter,
  resolvedHeader,
  resolvedLoadingState,
  resolvedState,
  workId,
}: {
  bestOffer: DealOffer | null;
  contextReleaseId?: string;
  currency: string;
  offers: DealOffer[];
  outOfStockOffer: DealOffer | null;
  presentationScope: DealOffersProps["presentationScope"];
  releaseId?: string;
  resolvedEmptyState?: ReactNode;
  resolvedErrorState?: ReactNode;
  resolvedFooter?: DealOffersProps["resolvedFooter"];
  resolvedHeader?: ReactNode;
  resolvedLoadingState?: ReactNode;
  resolvedState: ReturnType<typeof getDealOffersResolvedState>;
  workId?: string;
}) {
  const terminal = terminalDealOffersContent({
    emptyState: resolvedEmptyState,
    errorState: resolvedErrorState,
    loadingState: resolvedLoadingState,
    state: resolvedState,
  });

  if (terminal.terminal) {
    return (
      <>
        {resolvedHeader}
        {terminal.content}
        {resolvedState === "unavailable" &&
          resolvedFooter?.({ hasNewOffers: false, hasOffer: false })}
      </>
    );
  }

  if (!bestOffer && outOfStockOffer) {
    return (
      <>
        {resolvedHeader}
        <OutOfStockDealOffer
          currency={currency}
          offer={outOfStockOffer}
          presentationScope={presentationScope}
        />
        {resolvedFooter?.({ hasNewOffers: false, hasOffer: true })}
        <DealOfferStoreDisclosure />
      </>
    );
  }

  if (!bestOffer) return resolvedEmptyState;

  return (
    <>
      {resolvedHeader}
      <DealOffersBlock
        bestOffer={bestOffer}
        contextReleaseId={contextReleaseId}
        offers={offers}
        releaseId={releaseId}
        workId={workId}
        currency={currency}
        presentationScope={presentationScope}
      />
      {resolvedFooter?.({ hasNewOffers: true, hasOffer: true })}
      <DealOfferStoreDisclosure />
    </>
  );
}

function DirectDealOffersContent({
  presentationScope,
  releaseId,
  contextReleaseId,
  workId,
  format,
  resolvedFooter,
  resolvedHeader,
  resolvedEmptyState,
  resolvedErrorState,
  resolvedLoadingState,
}: DealOffersProps) {
  const { currency } = useCurrency();
  const { viewerId } = useViewerQueryState();
  const input = useDealOffersInput({ releaseId, workId, currency, format });
  const dealsQuery = useQuery(getDealOffersQueryOptions(input, { viewerId }));
  const outOfStockInput = directOutOfStockOffersInput({
    contextReleaseId,
    currency,
    format,
    releaseId,
    workId,
  });
  const outOfStockQuery = useQuery(
    getOutOfStockDealOffersQueryOptions(outOfStockInput, { viewerId }),
  );
  const offers = (dealsQuery.data?.offers ?? []) as DealOffer[];
  const bestOffer = (dealsQuery.data?.bestOffer ?? null) as DealOffer | null;
  const outOfStockOffer = (outOfStockQuery.data?.offers[0] ?? null) as DealOffer | null;
  const resolvedState = directDealOffersState({
    bestOffer,
    dealsError: dealsQuery.isError,
    dealsLoading: dealsQuery.isLoading,
    hasOffers: offers.length > 0,
    outOfStockError: outOfStockQuery.isError,
    outOfStockLoading: outOfStockQuery.isLoading,
    outOfStockOffer,
  });
  return (
    <DirectDealOffersResult
      bestOffer={bestOffer}
      contextReleaseId={contextReleaseId}
      currency={currency}
      offers={offers}
      outOfStockOffer={outOfStockOffer}
      presentationScope={presentationScope}
      releaseId={releaseId}
      resolvedEmptyState={resolvedEmptyState}
      resolvedErrorState={resolvedErrorState}
      resolvedFooter={resolvedFooter}
      resolvedHeader={resolvedHeader}
      resolvedLoadingState={resolvedLoadingState}
      resolvedState={resolvedState}
      workId={workId}
    />
  );
}

function SummaryDealOffersBlock({
  activeFormat,
  contextReleaseId,
  currency,
  format,
  formatLinkTarget,
  formatOptions,
  onFormatChange,
  presentationScope,
  selectedSummary,
  workId,
}: {
  activeFormat: FormatCategoryWithOther | null;
  contextReleaseId?: string;
  currency: string;
  format?: DealOffersProps["format"];
  formatLinkTarget?: DealOffersProps["formatLinkTarget"];
  formatOptions: DealOfferFormatOption[];
  onFormatChange: (format: FormatCategoryWithOther) => void;
  presentationScope: DealOffersProps["presentationScope"];
  selectedSummary: DealOfferSummary;
  workId: string;
}) {
  const bestOffer = dealOfferFromSummary(selectedSummary);
  const compareOfferCount = Math.max(0, selectedSummary.offerCount - 1);
  const compareFormat =
    selectedSummary.formatKey === "all" ? undefined : (bestOffer.formatCategory ?? undefined);
  const explorerInput: DealOffersQueryOptionsInput = contextReleaseId
    ? { workId, currency, format: compareFormat ?? format }
    : { workId, currency };
  return (
    <DealOffersBlock
      bestOffer={bestOffer}
      compareFormat={compareFormat}
      compareOfferCount={compareOfferCount}
      contextReleaseId={contextReleaseId}
      offers={[bestOffer]}
      queryInput={explorerInput}
      workId={workId}
      currency={currency}
      presentationScope={presentationScope}
      formatOptions={contextReleaseId ? undefined : formatOptions}
      formatLinkTarget={formatLinkTarget}
      activeFormat={activeFormat}
      onFormatChange={contextReleaseId ? undefined : onFormatChange}
    />
  );
}

function summaryDealOffersLoadState(input: {
  formatSummariesError: boolean;
  formatSummariesLoading: boolean;
  hasContextRelease: boolean;
  hasSelectedSummary: boolean;
  hasSnapshotSummary: boolean;
  outOfStockError: boolean;
  outOfStockLoading: boolean;
  snapshotLoading: boolean;
  summaryError: boolean;
  usesSnapshot: boolean;
}) {
  const loading = [
    input.snapshotLoading,
    !input.hasSnapshotSummary && input.outOfStockLoading,
    !input.hasContextRelease && input.formatSummariesLoading,
  ].some(Boolean);
  if (loading) return "loading" as const;

  const error = [
    !input.usesSnapshot && input.summaryError,
    !input.hasSelectedSummary && input.outOfStockError,
    !input.hasSelectedSummary && !input.hasContextRelease && input.formatSummariesError,
  ].some(Boolean);
  return error ? ("error" as const) : ("ready" as const);
}

function selectedSummaryDealOffers(input: {
  activeFormat: FormatCategoryWithOther | null;
  contextReleaseId?: string;
  formatSummaries: DealOfferSummary[];
  outOfStockOffers: DealOffer[];
  summary: DealOfferSummary | null | undefined;
}) {
  if (input.contextReleaseId) {
    return {
      selectedSummary: input.summary ?? null,
      selectedOutOfStockOffer: input.outOfStockOffers[0] ?? null,
    };
  }
  return {
    selectedSummary: selectedDealOfferSummary({
      activeFormat: input.activeFormat,
      formatSummaries: input.formatSummaries,
    }),
    selectedOutOfStockOffer: selectedOutOfStockDealOffer({
      activeFormat: input.activeFormat,
      offers: input.outOfStockOffers,
    }),
  };
}

function SummaryOutOfStockDealOffer({
  activeFormat,
  contextReleaseId,
  currency,
  formatOptions,
  formatLinkTarget,
  offer,
  onFormatChange,
  presentationScope,
}: {
  activeFormat: FormatCategoryWithOther | null;
  contextReleaseId?: string;
  currency: string;
  formatOptions: DealOfferFormatOption[];
  formatLinkTarget?: DealOffersProps["formatLinkTarget"];
  offer: DealOffer;
  onFormatChange: (format: FormatCategoryWithOther) => void;
  presentationScope: DealOffersProps["presentationScope"];
}) {
  return (
    <OutOfStockDealOffer
      currency={currency}
      formatSwitch={
        contextReleaseId ? undefined : (
          <DealOfferFormatTabs
            variant="segmented"
            activeFormat={activeFormat}
            formatLinkTarget={formatLinkTarget}
            options={formatOptions}
            onSelect={onFormatChange}
          />
        )
      }
      offer={offer}
      presentationScope={presentationScope}
    />
  );
}

function SummaryDealOffersResult({
  activeFormat,
  contextReleaseId,
  currency,
  format,
  formatOptions,
  formatLinkTarget,
  loadState,
  onFormatChange,
  presentationScope,
  resolvedEmptyState,
  resolvedErrorState,
  resolvedFooter,
  resolvedHeader,
  resolvedLoadingState,
  selectedOutOfStockOffer,
  selectedSummary,
  workId,
}: {
  activeFormat: FormatCategoryWithOther | null;
  contextReleaseId?: string;
  currency: string;
  format?: DealOffersProps["format"];
  formatOptions: DealOfferFormatOption[];
  formatLinkTarget?: DealOffersProps["formatLinkTarget"];
  loadState: "error" | "loading" | "ready";
  onFormatChange: (format: FormatCategoryWithOther) => void;
  presentationScope: DealOffersProps["presentationScope"];
  resolvedEmptyState?: ReactNode;
  resolvedErrorState?: ReactNode;
  resolvedFooter?: DealOffersProps["resolvedFooter"];
  resolvedHeader?: ReactNode;
  resolvedLoadingState?: ReactNode;
  selectedOutOfStockOffer: DealOffer | null;
  selectedSummary: DealOfferSummary | null;
  workId: string;
}) {
  if (loadState !== "ready") {
    return (
      <>
        {resolvedHeader}
        {loadState === "loading" ? (resolvedLoadingState ?? null) : (resolvedErrorState ?? null)}
      </>
    );
  }
  if (selectedSummary) {
    return (
      <>
        {resolvedHeader}
        <SummaryDealOffersBlock
          activeFormat={activeFormat}
          contextReleaseId={contextReleaseId}
          currency={currency}
          format={format}
          formatOptions={formatOptions}
          formatLinkTarget={formatLinkTarget}
          onFormatChange={onFormatChange}
          presentationScope={presentationScope}
          selectedSummary={selectedSummary}
          workId={workId}
        />
        {resolvedFooter?.({ hasNewOffers: true, hasOffer: true })}
        <DealOfferStoreDisclosure />
      </>
    );
  }
  if (selectedOutOfStockOffer) {
    return (
      <>
        {resolvedHeader}
        <SummaryOutOfStockDealOffer
          activeFormat={activeFormat}
          contextReleaseId={contextReleaseId}
          currency={currency}
          formatOptions={formatOptions}
          formatLinkTarget={formatLinkTarget}
          offer={selectedOutOfStockOffer}
          onFormatChange={onFormatChange}
          presentationScope={presentationScope}
        />
        {resolvedFooter?.({ hasNewOffers: false, hasOffer: true })}
        <DealOfferStoreDisclosure />
      </>
    );
  }
  return (
    <DirectDealOffersContent
      presentationScope={presentationScope}
      contextReleaseId={contextReleaseId}
      workId={workId}
      format={format}
      resolvedFooter={resolvedFooter}
      resolvedHeader={resolvedHeader}
      resolvedEmptyState={resolvedEmptyState}
      resolvedErrorState={resolvedErrorState}
      resolvedLoadingState={resolvedLoadingState}
    />
  );
}

function SummaryDealOffersContent({
  presentationScope,
  contextReleaseId,
  offerSummarySnapshot,
  workId,
  format,
  formatLinkTarget,
  resolvedFooter,
  resolvedHeader,
  resolvedEmptyState,
  resolvedErrorState,
  resolvedLoadingState,
}: DealOffersProps & { workId: string }) {
  const { currency } = useCurrency();
  const { viewerId } = useViewerQueryState();
  const summaryInput = { workId, releaseId: contextReleaseId, currency, format };
  const summaryQuery = useQuery({
    ...getDealOfferSummaryQueryOptions(summaryInput, { viewerId }),
    enabled: !offerSummarySnapshot,
  });
  const formatSummariesQuery = useQuery({
    ...getDealOfferFormatSummariesQueryOptions({ workId, currency }, { viewerId }),
    enabled: !contextReleaseId,
  });
  const outOfStockQuery = useQuery(
    getOutOfStockDealOffersQueryOptions(
      contextReleaseId ? { releaseId: contextReleaseId, currency } : { workId, currency },
      { viewerId },
    ),
  );
  const snapshot: DealOfferSummarySnapshot = offerSummarySnapshot ?? {
    isLoading: summaryQuery.isLoading,
    summary: summaryQuery.data?.[0] ?? null,
  };
  const summary = snapshot.summary as DealOfferSummary | null | undefined;
  const formatSummaries = useMemo(
    () => (formatSummariesQuery.data ?? []) as DealOfferSummary[],
    [formatSummariesQuery.data],
  );
  const outOfStockOffers = useMemo(
    () => (outOfStockQuery.data?.offers ?? []) as DealOffer[],
    [outOfStockQuery.data?.offers],
  );
  const requestedFormat = requestedDealOfferFormat(format);
  const formatOptions = useMemo<DealOfferFormatOption[]>(
    () => dealOfferFormatOptions(formatSummaries, outOfStockOffers),
    [formatSummaries, outOfStockOffers],
  );
  const preferredFormat = useMemo(
    () =>
      contextReleaseId
        ? null
        : preferredDealOfferFormat({ options: formatOptions, requestedFormat, summary }),
    [contextReleaseId, formatOptions, requestedFormat, summary],
  );
  const [activeFormat, setActiveFormat] = useState<FormatCategoryWithOther | null>(null);

  useEffect(() => {
    setActiveFormat(preferredFormat ?? null);
  }, [currency, preferredFormat, workId]);

  const resolvedActiveFormat = activeFormat ?? preferredFormat;
  const selection = selectedSummaryDealOffers({
    activeFormat: resolvedActiveFormat,
    contextReleaseId,
    formatSummaries,
    outOfStockOffers,
    summary,
  });
  const loadState = summaryDealOffersLoadState({
    formatSummariesError: formatSummariesQuery.isError,
    formatSummariesLoading: formatSummariesQuery.isLoading,
    hasContextRelease: Boolean(contextReleaseId),
    hasSelectedSummary: Boolean(selection.selectedSummary),
    hasSnapshotSummary: Boolean(summary),
    outOfStockError: outOfStockQuery.isError,
    outOfStockLoading: outOfStockQuery.isLoading,
    snapshotLoading: snapshot.isLoading,
    summaryError: summaryQuery.isError,
    usesSnapshot: Boolean(offerSummarySnapshot),
  });
  return (
    <SummaryDealOffersResult
      activeFormat={resolvedActiveFormat}
      contextReleaseId={contextReleaseId}
      currency={currency}
      format={format}
      formatOptions={formatOptions}
      formatLinkTarget={formatLinkTarget}
      loadState={loadState}
      onFormatChange={setActiveFormat}
      presentationScope={presentationScope}
      resolvedEmptyState={resolvedEmptyState}
      resolvedErrorState={resolvedErrorState}
      resolvedHeader={resolvedHeader}
      resolvedLoadingState={resolvedLoadingState}
      selectedOutOfStockOffer={selection.selectedOutOfStockOffer}
      selectedSummary={selection.selectedSummary}
      workId={workId}
      resolvedFooter={(state) => (
        <>
          <UsedOfferSection
            input={{ workId, currency, format: resolvedActiveFormat ?? format }}
            hasNewOffers={state.hasNewOffers}
            showStoreDisclosure={!state.hasOffer}
          />
          {resolvedFooter?.(state)}
        </>
      )}
    />
  );
}

function DealOffersContent(props: DealOffersProps) {
  if (props.presentationScope === "release" && (props.contextReleaseId || props.releaseId)) {
    return <ReleaseDealOffersContent {...props} />;
  }
  if (props.workId && !props.releaseId) {
    return <SummaryDealOffersContent {...props} workId={props.workId} />;
  }

  return <DirectDealOffersContent {...props} />;
}

export function TrackedDealOffers(props: DealOffersProps) {
  const isPartnerShare = useIsPartnerShare();
  if (!shouldRenderTrackedOffers(isPartnerShare)) return null;

  return <DealOffersContent {...props} />;
}
